// Đăng nhập Office 365 bằng MSAL (SPA + PKCE, không client secret). Token dùng để gọi worker `api`.
import {
  InteractionRequiredAuthError,
  createStandardPublicClientApplication,
  type AccountInfo,
  type IPublicClientApplication,
} from '@azure/msal-browser';
import { AAD } from './auth-config';

const WORKERS_DEV = 'https://hana-hub-api.giangkoi89.workers.dev';
/** Trên hub.hanalogisticsvn.com gọi cùng tên miền (route `/api/*` → worker, A-10, không CORS); bản preview / localhost vẫn qua workers.dev. */
export const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? (location.hostname === 'hub.hanalogisticsvn.com' ? location.origin : WORKERS_DEV);

let pca: Promise<IPublicClientApplication> | undefined;
function msal(): Promise<IPublicClientApplication> {
  return (pca ??= createStandardPublicClientApplication({
    auth: {
      clientId: AAD.clientId,
      authority: AAD.authority,
      redirectUri: location.origin + AAD.redirectPath,
      postLogoutRedirectUri: location.origin + '/',
    },
    cache: { cacheLocation: 'localStorage' },
    system: { iframeBridgeTimeout: 10_000 },
  }));
}

const ERR_KEY = 'hana.auth.err';

/** Mã lỗi để chẩn đoán (AADSTS… / tên lỗi MSAL), không chứa token. */
export function authErrorCode(e: unknown): string {
  const msg = e instanceof Error ? e.message : String(e ?? '');
  const aad = /AADSTS\d+/.exec(msg)?.[0];
  if (aad) return aad;
  const code = (e as { errorCode?: string } | null)?.errorCode;
  if (code) return code;
  return e instanceof Error ? e.name : 'unknown';
}

/** Ghi lỗi đăng nhập ra console và sessionStorage (qua lượt chuyển trang /auth/callback → /). */
export function recordAuthError(e: unknown): string {
  const code = authErrorCode(e);
  console.error('[auth]', e);
  try {
    sessionStorage.setItem(ERR_KEY, code);
  } catch {
    /* không có sessionStorage thì thôi */
  }
  return code;
}

export function lastAuthError(): string | null {
  try {
    return sessionStorage.getItem(ERR_KEY);
  } catch {
    return null;
  }
}

/** Xoá trạng thái "đang tương tác" MSAL bị kẹt (bấm 2 lần / back giữa chừng). */
function clearInteraction(): void {
  for (const st of [localStorage, sessionStorage]) {
    try {
      for (const k of Object.keys(st)) if (k.includes('interaction.status')) st.removeItem(k);
    } catch {
      /* bỏ qua */
    }
  }
}

/** Chuyển sang trang đăng nhập Microsoft. `ret` = vùng cần quay lại sau khi đăng nhập. */
export async function signIn(ret?: string): Promise<void> {
  const p = await msal();
  const req = { scopes: [AAD.apiScope], prompt: 'select_account', state: ret ?? '' };
  try {
    await p.loginRedirect(req);
  } catch (e) {
    if (authErrorCode(e) !== 'interaction_in_progress') throw e;
    clearInteraction();
    await p.loginRedirect(req);
  }
}

export type SignInResult = { ok: true; ret: string } | { ok: false; reason: 'notcompany' | 'failed' | 'none' };

/** Chạy ở /auth/callback: nhận kết quả Microsoft trả về. */
export async function completeSignIn(): Promise<SignInResult> {
  const p = await msal();
  try {
    const r = await p.handleRedirectPromise({ navigateToLoginRequestUrl: false });
    if (!r?.account) return { ok: false, reason: 'none' };
    if (r.account.tenantId !== AAD.tenantId) return { ok: false, reason: 'notcompany' };
    p.setActiveAccount(r.account);
    return { ok: true, ret: r.state ?? '' };
  } catch (e) {
    // AADSTS50020 / 50034: tài khoản ngoài tenant HANA
    const msg = e instanceof Error ? e.message : '';
    recordAuthError(e);
    return { ok: false, reason: /AADSTS(50020|50034|500200)/.test(msg) ? 'notcompany' : 'failed' };
  }
}

/** Chỉ khi chạy `vite dev` (không có trong bản build): `localStorage['hana.demo']='1'` giả lập đăng nhập để
 *  chụp màn so với board (tooling/boards/shot.mjs). Bản deploy không có nhánh này. */
const DEMO: AccountInfo | null = import.meta.env.DEV
  ? ({ homeAccountId: 'demo', environment: 'demo', tenantId: AAD.tenantId, username: 'david@hanalogisticsvn.com', localAccountId: 'demo', name: 'DAVID' } as AccountInfo)
  : null;
function demoOn(): boolean {
  try {
    return !!DEMO && localStorage.getItem('hana.demo') === '1';
  } catch {
    return false;
  }
}

export async function currentAccount(): Promise<AccountInfo | null> {
  if (demoOn()) return DEMO;
  const p = await msal();
  const a = p.getActiveAccount() ?? p.getAllAccounts().find((x) => x.tenantId === AAD.tenantId) ?? null;
  if (a && !p.getActiveAccount()) p.setActiveAccount(a);
  return a;
}

export async function signOut(): Promise<void> {
  const p = await msal();
  const account = p.getActiveAccount();
  await p.logoutRedirect(account ? { account } : undefined);
}

async function apiToken(): Promise<string> {
  const p = await msal();
  const account = await currentAccount();
  if (!account) throw new Error('signed_out');
  try {
    return (await p.acquireTokenSilent({ scopes: [AAD.apiScope], account, redirectUri: location.origin + AAD.silentRedirectPath })).accessToken;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) {
      await p.acquireTokenRedirect({ scopes: [AAD.apiScope], account, state: '' });
    }
    throw e;
  }
}

const API_TIMEOUT = 15_000;

/** Gọi worker `api` kèm token Office 365. Một hạn 15 giây cho cả lấy token lẫn fetch: quá hạn → lỗi, màn ra state `error` thay vì treo. */
export async function api<T>(path: string): Promise<T> {
  const signal = AbortSignal.timeout(API_TIMEOUT);
  const token = await Promise.race([
    apiToken(),
    new Promise<never>((_, rej) => signal.addEventListener('abort', () => rej(new Error('token_timeout')), { once: true })),
  ]);
  const r = await fetch(API_BASE + path, { headers: { Authorization: `Bearer ${token}` }, signal });
  if (!r.ok) throw Object.assign(new Error(`api ${r.status}`), { status: r.status });
  return (await r.json()) as T;
}
